import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';
import { featureFlags, isFeatureActive } from '@/shared/config';
import { readApiErrorDiagnostics } from './apiErrorDiagnostics';

/**
 * Sentry 태그를 붙이는 자리를 한 곳에 모은다.
 *
 * 태그가 둘로 나뉜다. 창·팀·기능 플래그·시나리오는 **범위 태그**라 한 번 붙으면 그 뒤의 모든
 * 이벤트에 실리고, 요청 주소·방식은 **이벤트 태그**라 `beforeSend` 에서 그 이벤트에만 붙는다.
 * 붙는 방식이 달라 함수는 나뉘지만 태그 이름은 여기 한 파일에만 있다 — 흩어지면 같은 축에
 * 두 이름이 생긴다. `sentry.ts` 는 이 함수들을 불러 쓰고 바깥에 그대로 다시 내보낸다.
 */

// ─── 범위 태그 ───────────────────────────────────────────────────────────────

let currentWindowId: string | undefined;

/** 지금 열린 창. 403 이 창 안에서 났는지 가를 때 읽는다. */
export function getWindowTag(): string | undefined {
  return currentWindowId;
}

/** 창 전환. 태그와 컨텍스트만 — 자취와 span 은 `sentry.ts` 가 맡는다. */
export function setWindowTag(windowId: string): void {
  currentWindowId = windowId;
  Sentry.setTag('window', windowId);
  Sentry.setContext('window', { id: windowId });
}

export interface BusinessContext {
  teamId?: string;
  teamRole?: string;
  roles?: readonly string[];
}

/**
 * 비즈니스 도메인 태그를 Sentry scope에 설정한다.
 * 호출 지점: 로그인 성공 직후 (authStore.setAuth)
 * — feature_flags / team_id / team_role / roles 태그를 발행해서, 에러를 어떤 비즈니스 흐름에서
 *   누가 만났는지를 stack trace 옆에서 바로 식별할 수 있게 한다.
 */
export function setBusinessContext(context: BusinessContext): void {
  if (context.teamId !== undefined) {
    Sentry.setTag('team_id', context.teamId);
  }
  if (context.teamRole !== undefined) {
    Sentry.setTag('team_role', context.teamRole);
  }
  if (context.roles !== undefined && context.roles.length > 0) {
    Sentry.setTag('roles', context.roles.join(','));
  }
  Sentry.setTag('feature_flags', getActiveFeatureFlagsSignature());
}

/**
 * 시나리오/퀘스트 진입 시점 컨텍스트.
 * null을 넘기면 시나리오 컨텍스트를 클리어한다 (시나리오 종료/이탈 시).
 */
export function setScenarioContext(scenario: { id: string; step?: string | number } | null): void {
  if (scenario === null) {
    Sentry.setContext('scenario', null);
    return;
  }
  Sentry.setContext('scenario', scenario);
  Sentry.setTag('scenario_id', scenario.id);
}

/**
 * 활성 feature flag 의 정렬된 시그니처. 같은 플래그 조합이면 같은 문자열이 나오므로,
 * Sentry에서 "이 플래그 조합에서만 발생한다" 류의 그룹 분석이 가능해진다.
 */
function getActiveFeatureFlagsSignature(): string {
  const active = Object.keys(featureFlags)
    .filter((key) => isFeatureActive(key))
    .sort();
  return active.length > 0 ? active.join(',') : 'none';
}

// ─── 이벤트 태그 ─────────────────────────────────────────────────────────────

/**
 * API 오류 이벤트에 요청 주소·방식 태그와 엔드포인트 지문을 얹는다.
 *
 * 404 는 `shouldCaptureApiError` 가 false 를 돌려주는 무리라 axios 인터셉터의 `captureApiError`
 * 를 타지 않는다. 그래서 주소를 싣는 `contexts.api` 가 404 이벤트에는 없고, 남는 경로인
 * `queryClient` 의 두 자리는 상태 코드만 태그로 단다. 지문도 없어 Sentry 가 스택으로 묶는데
 * 그 스택이 두 자리뿐이라 상태 코드가 달라도 한 이슈가 된다(AXIOM-WEB-2 에 여덟 종이 들어 있다).
 * 캡처 경로가 전부 지나는 자리는 `beforeSend` 하나뿐이라 여기서 얹는다.
 */

const IDENTIFIER_SEGMENT =
  /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|\d+)$/;

interface AxiosLikeError {
  config?: { url?: string; method?: string };
  response?: { status?: number; data?: unknown };
}

function isAxiosLikeError(value: unknown): value is AxiosLikeError {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { isAxiosError?: unknown }).isAxiosError === true
  );
}

/**
 * 요청 주소를 태그·지문에 쓸 엔드포인트로 줄인다.
 * 식별자를 `:id` 로 접는 것이 핵심이다 — 접지 않으면 이슈가 식별자 수만큼 쪼개져
 * 지금(전부 한 덩어리)의 반대쪽으로 망가진다. 같은 이유의 선례가 `trackPageView.ts` 에 있다.
 */
export function toEndpoint(url: string): string {
  const withoutOrigin = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
  const path = withoutOrigin.split(/[?#]/)[0] ?? '';
  return path
    .split('/')
    .map((segment) => (IDENTIFIER_SEGMENT.test(segment) ? ':id' : segment))
    .join('/');
}

export function applyApiErrorFingerprint(event: ErrorEvent, hint?: EventHint): ErrorEvent {
  const error = hint?.originalException;
  if (!isAxiosLikeError(error)) return event;

  const url = error.config?.url;
  const endpoint = toEndpoint(url ?? '');
  const method = (error.config?.method ?? '').toUpperCase() || 'UNKNOWN';
  const status = error.response?.status;

  // Cache-level 400 captures bypass captureApiError. Enrich every capture here,
  // without collecting request/response bodies or validation message values.
  if (status === 400) {
    const diagnostics = readApiErrorDiagnostics(error.response?.data);
    event.tags = { ...event.tags, error_code: diagnostics.error_code };
    event.contexts = {
      ...event.contexts,
      api: { endpoint: endpoint || 'unknown', method, status, ...diagnostics },
    };
  }
  if (!url) return event;

  event.tags = {
    ...event.tags,
    status_code: status ? String(status) : 'network',
    request_url: endpoint,
    request_method: method,
  };
  event.fingerprint = ['api', status ? String(status) : 'network', method, endpoint];

  return event;
}
